/**
 * The first online hour, client side (WP-N2 / WP-N4, docs/ONLINE-UI.md "The first evening"): the glue between the
 * online store and the pure rules in src/onboarding —
 *
 *  - "Your record" from the server (`characterRecord`, read-only), asked for on join and when something it
 *    counts has happened (a job paid, a freight run over), never more than every few seconds;
 *  - the first-evening ladder's facts (onlineLadder.ts): where the loaner stands, the nearest board, the bank,
 *    Sid's door, a pub, the pier, a lockup — and two things only this device sees (you've driven, you've stood
 *    at Sid's door), remembered per character in localStorage (`bh.firstHour.v1`);
 *  - progressive disclosure (disclosure.ts): which advanced parts of the book and the hotkey row show, and a
 *    "New in your book: …" note (and toast) the moment one opens. A part that was ever shown stays shown.
 *
 * Nothing here decides money, heat or items; it only chooses what to show.
 */
import { BANKS, GARAGE, LOCKUPS } from '../../shared/economy';
import type { CharacterRecordView } from '../../shared/messages/economy';
import type { LennyListView } from '../../shared/messages/lenny';
import { FREIGHT } from '../../shared/messages/activities';
import { FEATURE_NOTE, newlyShown, shownFeatures, type DisclosureFacts, type Feature } from '../onboarding/disclosure';
import { ladderRung, type LadderFacts } from '../onboarding/onlineLadder';
import type { Waypoint } from '../onboarding/nextStep';
import { nearest } from '../onboarding/nextStep';
import type { OnlineGame } from './game';

const STORE_KEY = 'bh.firstHour.v1';
/** Seconds between record requests at most, and between disclosure checks. */
const RECORD_GAP_S = 8;
const CHECK_GAP_S = 0.5;
/** Standing this close to Sid's door counts as having been to see him. */
const SID_REACH = 12;

interface CharMemory {
  drove: boolean;
  sid: boolean;
  shown: Feature[];
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The shared first-hour state (one per page, like `invites`). */
class FirstHour {
  record: CharacterRecordView | null = null;
  /** Bumped whenever what shows changes (the hotkey row and the book redraw on it). */
  version = 0;
  private shown = new Set<Feature>();
  private mem: Record<string, CharMemory> = {};
  private who = '';
  private ladder: LadderFacts | null = null;
  /** Shown set computed at least once since joining (the first pass is silent: no notes for what a veteran has). */
  private primed = false;

  constructor() {
    try {
      const raw = storage()?.getItem(STORE_KEY);
      const d = raw ? (JSON.parse(raw) as Record<string, Partial<CharMemory>>) : {};
      for (const [k, v] of Object.entries(d).slice(-40)) {
        if (!v || typeof v !== 'object') continue;
        this.mem[k] = { drove: v.drove === true, sid: v.sid === true, shown: Array.isArray(v.shown) ? (v.shown.filter((x) => typeof x === 'string') as Feature[]) : [] };
      }
    } catch {
      /* nothing remembered */
    }
  }

  private save(): void {
    try {
      storage()?.setItem(STORE_KEY, JSON.stringify(this.mem));
    } catch {
      /* not kept */
    }
  }

  private me(): CharMemory {
    return (this.mem[this.who] ??= { drove: false, sid: false, shown: [] });
  }

  /** A new session (or another character): forget the live state, keep the memory. */
  join(who: string): void {
    this.who = who;
    this.record = null;
    this.ladder = null;
    this.primed = false;
    this.shown = new Set(this.me().shown);
    this.version++;
  }

  /** Does `feature` show? No feature (the everyday hotkeys) always shows. */
  shows(feature: string | undefined): boolean {
    return !feature || this.shown.has(feature as Feature);
  }

  /** Opened from a key or a message: it shows from now on. */
  use(feature: string | undefined): void {
    // Warn the crew comes and goes with a run on; pressing Z off a run changes nothing.
    if (!feature || feature === 'warn' || !this.who || this.shown.has(feature as Feature)) return;
    this.shown.add(feature as Feature);
    this.remember();
    this.version++;
  }

  private remember(): void {
    this.me().shown = [...this.shown].filter((k) => k !== 'warn');
    this.save();
  }

  noteDrove(): void {
    const m = this.me();
    if (this.who && !m.drove) {
      m.drove = true;
      this.save();
    }
  }

  noteSid(): void {
    const m = this.me();
    if (this.who && !m.sid) {
      m.sid = true;
      this.save();
    }
  }

  /** The ladder's facts as last worked out (null before the first frame online). */
  ladderFacts(): LadderFacts | null {
    return this.ladder;
  }

  rung(): number | null {
    return this.ladder ? ladderRung(this.ladder) : null;
  }

  /** Recompute the shown set; returns the parts that just opened (for the notes). */
  update(ladder: LadderFacts, facts: Omit<DisclosureFacts, 'ladder'>): Feature[] {
    this.ladder = ladder;
    const now = shownFeatures({ ...facts, ladder }, this.shown);
    const fresh = this.primed ? newlyShown(now, this.shown) : [];
    const changed = now.size !== this.shown.size || [...now].some((k) => !this.shown.has(k));
    this.primed = true;
    if (changed) {
      this.shown = now;
      this.remember();
      this.version++;
    }
    return fresh;
  }

  memory(): CharMemory {
    return this.me();
  }
}

export const firstHour = new FirstHour();

/** Wire the first hour to the online game: record requests, the ladder facts, the disclosure notes. */
export function registerFirstHour(game: OnlineGame): void {
  const ctx = game.ctx;
  let recordAt = -Infinity;
  let recordDue = false;
  let checkIn = 0;
  let clock = 0;
  let lastJob = false;
  let lastFreight = false;
  let lastCash: number | null = null;

  const askRecord = (): void => {
    recordDue = true;
  };
  ctx.events.on('netJoined', () => {
    firstHour.join(ctx.services.tryGet('net')?.session?.character.name ?? '');
    // A new session: the first standing that arrives is taken silently (no calls for rungs a veteran already has).
    ctx.services.tryGet('contacts')?.setOnline(null);
    recordAt = -Infinity;
    askRecord();
  });
  game.store.subscribe((keys) => {
    const s = game.store.state;
    if (keys.has('econ')) {
      const job = !!s.econ?.job;
      if (lastJob && !job) askRecord(); // a job ended (paid or not)
      lastJob = job;
      // Money moved (a sale, a purchase, Pike's work): the contact ladders may have moved too (throttled below).
      const money = s.econ ? s.econ.cash + s.econ.bank : null;
      if (money !== null && money !== lastCash) askRecord();
      lastCash = money;
    }
    if (keys.has('freight')) {
      const on = !!s.freight && s.freight.status !== 'completed';
      if (lastFreight && !on) askRecord();
      lastFreight = on;
    }
  });

  const poi = (id: string): Waypoint | undefined => {
    const p = ctx.services.tryGet('layout')?.pois.find((x) => x.id === id);
    return p ? { x: p.pos.x, z: p.pos.z, label: p.name } : undefined;
  };

  function ladderFacts(): LadderFacts {
    const s = game.store.state;
    const mem = firstHour.memory();
    const me = game.playerPos();
    const layout = ctx.services.tryGet('layout');
    const pois = layout?.pois ?? [];
    const rec = firstHour.record;
    const owned = s.econ?.vehicles ?? [];
    const loanerRow = owned.find((v) => v.loaner);
    // The loaner out on the street: the car of ours nearest to us with its model (only one is lent at a time).
    let loaner: Waypoint | undefined;
    let loanerModel: string | undefined;
    if (loanerRow?.status === 'in_world') {
      let bd = Infinity;
      for (const v of ctx.services.tryGet('vehicles')?.all() ?? []) {
        if (!v.ownedByPlayer || v.spec.id !== loanerRow.spec || v.destroyed) continue;
        const d = Math.hypot(v.pos.x - me.x, v.pos.z - me.z);
        if (d < bd) {
          bd = d;
          loaner = { x: v.pos.x, z: v.pos.z, label: 'Halloran’s loaner' };
          loanerModel = v.spec.displayName;
        }
      }
    }
    const fence = ctx.services.tryGet('weapons')?.fence;
    const weapons = ctx.services.tryGet('weapons')?.owned() ?? [];
    const pubs = pois.filter((p) => p.kind === 'pub').map((p) => ({ x: p.pos.x, z: p.pos.z, label: p.name }));
    // The nearest counter that takes money (the bank, or a pub's / phone box's cashpoint), not always the bank
    // across town: the first job ends at a board that very often has a cashpoint of its own.
    const counters = BANKS.map((b) => {
      const w = poi(b.poi);
      return w ? { ...w, label: b.name } : undefined;
    }).filter((w): w is Waypoint => !!w);
    const bank = nearest(counters, me);
    const sites = LOCKUPS.map((l) => poi(l.poi)).filter((w): w is Waypoint => !!w);
    const crew = s.crew;
    const meId = ctx.services.tryGet('net')?.session?.character.id;
    const turfYours = !!s.territory?.districts.some((d) => d.youLead);
    return {
      drove: mem.drove || owned.some((v) => !v.loaner),
      ...(loaner ? { loaner } : {}),
      ...(loanerModel ? { loanerModel } : {}),
      ...(poi(GARAGE.poi) ? { garage: poi(GARAGE.poi)! } : {}),
      jobs: rec?.jobs ?? 0,
      banked: (s.econ?.bank ?? 0) > 0,
      ...(bank ? { bank } : {}),
      sid: mem.sid || weapons.some((w) => w !== 'fists'),
      ...(fence ? { sidAt: { x: fence.pos.x, z: fence.pos.z, label: 'Sid’s back room' } } : {}),
      crewed: !!crew || !!s.gang,
      ...(nearest(pubs, me) ? { pub: nearest(pubs, me)! } : {}),
      freightRuns: rec?.crewRuns ?? 0,
      crewOnline: crew ? crew.members.filter((m) => m.online || m.cid === meId).length : 0,
      ...(poi(FREIGHT.pierPoi) ? { pier: poi(FREIGHT.pierPoi)! } : {}),
      inGang: !!s.gang,
      premises: s.props.length > 0 || turfYours || (rec?.districtsLed ?? 0) > 0,
      ...(nearest(sites, me) ? { site: nearest(sites, me)! } : {}),
      // The shard roster counts you too: 1 = nobody else in the city (0 = not heard yet).
      alone: ctx.services.tryGet('net')?.population === 1,
    };
  }

  game.onFrame((dt) => {
    clock += dt;
    const player = ctx.services.tryGet('player');
    if (player?.mode === 'driving') firstHour.noteDrove();
    const fence = ctx.services.tryGet('weapons')?.fence;
    if (player && fence && Math.hypot(player.pos.x - fence.pos.x, player.pos.z - fence.pos.z) < SID_REACH) firstHour.noteSid();
    if (recordDue && clock - recordAt >= RECORD_GAP_S && game.store.state.ready) {
      recordDue = false;
      recordAt = clock;
      void game.request<CharacterRecordView>('characterRecord', {}, { quiet: true }).then((r) => {
        if (r.ok && r.data) firstHour.record = r.data;
        // Contact ladders (WP-X1): the server's standing drives the book's ladders, price tags and the notes.
        if (r.ok && r.data?.contacts) ctx.services.tryGet('contacts')?.setOnline(r.data.contacts.pts, r.data.contacts.today);
        // Lenny's list (WP-X8): today's errands, settled by the server just before it answered.
        if (r.ok && r.data?.lenny) firstHourHooks.lenny(r.data.lenny);
        // A server without the record (or a refusal): an empty one, so the ladder and the book still work.
        else firstHour.record ??= { playTimeS: 0, jobs: 0, earned: 0, crewRuns: 0, crates: 0, arrests: 0, carsStolen: 0, bestEscape: 0, eventsWon: 0, districtsLed: 0 };
        firstHour.version++;
        if (ctx.services.tryGet('book')?.isOpen) ctx.services.tryGet('book')?.refresh();
      });
    }
    checkIn -= dt;
    if (checkIn > 0) return;
    checkIn = CHECK_GAP_S;
    const s = game.store.state;
    if (!s.ready || !firstHour.record) return; // wait for the record: the ladder's rungs need it
    const fresh = firstHour.update(ladderFacts(), {
      inCrew: !!s.crew,
      inGang: !!s.gang,
      freightOn: !!s.freight && s.freight.status !== 'completed',
      jobOn: !!s.firmJob && s.firmJob.status !== 'completed',
      props: s.props.length,
      gangProps: s.biz?.held ?? 0,
      turfYours: !!s.territory?.districts.some((d) => d.youLead || (d.contest && (d.contest.challenger.you || d.youLead))),
      access: {
        markets: (ctx.services.tryGet('contacts')?.perk('access-markets') ?? 0) > 0,
        turf: (ctx.services.tryGet('contacts')?.perk('access-turf') ?? 0) > 0,
        lenny: (ctx.services.tryGet('contacts')?.rung('lenny') ?? 0) >= 1,
      },
    });
    for (const k of fresh) {
      const n = FEATURE_NOTE[k]!;
      ctx.services.tryGet('book')?.post({ key: `new-in-book:${k}`, from: 'Your book', kind: 'tip', subject: `New in your book: ${n.name}`, text: n.text });
    }
    if (fresh.length) game.toast(`New in your book: ${fresh.map((k) => FEATURE_NOTE[k]!.name).join(', ')}`, 'info', 4);
    if (fresh.length && ctx.services.tryGet('book')?.isOpen) ctx.services.tryGet('book')?.refresh();
  });

  // Ask again when the book turns to the record page (bookFeed calls this).
  firstHourHooks.askRecord = askRecord;
}

/** Callbacks the book feed uses (set once the game is registered); `lenny`: Lenny's list heard with the record (lennyUi.ts). */
export const firstHourHooks: { askRecord: () => void; lenny: (v: LennyListView) => void } = { askRecord: () => undefined, lenny: () => undefined };
